import { TableCell, TableRow } from '@mui/material'
import * as React from 'react'
import {
  Datagrid,
  DatagridBody,
  DatagridBodyProps,
  DatagridProps,
  DatagridRowProps,
  FieldProps,
  Identifier,
  RecordContextProvider,
} from 'react-admin'
import { bgGray } from 'shared/theme/colors'
import styled from 'styled-components'

// Small padding gets overwritten in this implementation
const CTTableCell = styled(TableCell)`
  padding: 6px 24px 6px 16px !important;
`

// Missing styles
const CTTableRow = styled(TableRow)`
  cursor: pointer !important;

  &:hover {
    background-color: ${bgGray} !important;
  }
`

export type OnRowClickType = (
  id: Identifier,
  basePath: string | undefined,
  record: Record<string, any> | undefined,
  target: '_blank' | '_self'
) => void

const CTDatagridRow = ({
  record,
  id,
  children,
  className,
  classes,
  basePath,
  onRowClick,
  style,
}: DatagridRowProps & { onRowClick?: OnRowClickType }) =>
  id ? (
    <RecordContextProvider value={record}>
      <CTTableRow
        classes={classes}
        className={className}
        style={style}
        onAuxClick={(event) => {
          if (event.button === 1) {
            if (onRowClick) {
              onRowClick(id, basePath, record, '_blank')
              return
            }

            window.open(`${basePath}/${id}/show`, '_blank')
            return
          }
        }}
        onClick={(event) => {
          if (onRowClick) {
            onRowClick(id, basePath, record, event.metaKey || event.ctrlKey ? '_blank' : '_self')
            return
          }

          if (event.metaKey || event.ctrlKey) {
            window.open(`${basePath}/${id}/show`, '_blank')
            return
          }

          window.open(`${basePath}/${id}/show`, '_self')
          return
        }}
      >
        {/* data columns based on children */}
        {React.Children.map(children, (field) => {
          if (!React.isValidElement<FieldProps>(field)) {
            return null
          }

          // for FunctionFields there's a custom render
          // @ts-ignore
          if (field.props.render)
            // there's not always a source for FunctionFields
            return (
              <CTTableCell key={`${id}-${field.props.source || field.props.label}`} size="small">
                {
                  // @ts-ignore
                  field.props.render(record)
                }
              </CTTableCell>
            )

          if (field.props.source)
            return (
              <CTTableCell key={`${id}-${field.props.source}`} size="small">
                {React.cloneElement(field, { record, basePath, ...field.props })}
              </CTTableCell>
            )

          return null
        })}
      </CTTableRow>
    </RecordContextProvider>
  ) : null

const CTDatagridBody = ({ onRowClick, ...props }: DatagridBodyProps & { onRowClick?: OnRowClickType }) => (
  <DatagridBody {...props} row={<CTDatagridRow onRowClick={onRowClick} />} />
)

// Custom data grid component for handling row clicks
// SEE: https://marmelab.com/react-admin/Datagrid.html#body
export function CTDatagrid({ onRowClick, ...props }: DatagridProps & { onRowClick?: OnRowClickType }) {
  return <Datagrid {...props} body={<CTDatagridBody onRowClick={onRowClick} />} />
}
